import React, { useCallback } from 'react';
import { Button, Table, Input, InputNumber, Checkbox, Form, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import LookupField from '../../../components/LookupField';
import { J212FormData, FJ211FormItem } from '../hooks/useJ212Record';

interface Props {
  formData: J212FormData;
  details: FJ211FormItem[];
  isEditing: boolean;
  selectedRowIdx: number;
  onSelectRow: (idx: number) => void;
  setField: <K extends keyof J212FormData>(field: K, value: J212FormData[K]) => void;
  emptyDetail: () => FJ211FormItem;
}

const Tab2Details: React.FC<Props> = ({
  formData,
  details,
  isEditing,
  selectedRowIdx,
  onSelectRow,
  setField,
  emptyDetail,
}) => {
  const handleAddRow = useCallback(() => {
    setField('details', [...details, emptyDetail()]);
  }, [details, setField, emptyDetail]);

  const handleRemoveRow = useCallback(
    (index: number) => {
      const next = details.filter((_, i) => i !== index);
      setField('details', next);
    },
    [details, setField]
  );

  const handleCellChange = useCallback(
    <K extends keyof FJ211FormItem>(index: number, field: K, value: FJ211FormItem[K]) => {
      const next = details.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      setField('details', next);
    },
    [details, setField]
  );

  // Selected detail row for below-grid fields
  const sel = selectedRowIdx >= 0 && selectedRowIdx < details.length ? details[selectedRowIdx] : null;

  const handleDetailField = useCallback(
    <K extends keyof FJ211FormItem>(field: K, value: FJ211FormItem[K]) => {
      if (selectedRowIdx < 0 || selectedRowIdx >= details.length) return;
      const next = details.map((row, i) =>
        i === selectedRowIdx ? { ...row, [field]: value } : row
      );
      setField('details', next);
    },
    [details, selectedRowIdx, setField]
  );

  // Grid columns — 8 columns matching FoxPro grid (displayOrder)
  const columns = [
    {
      title: <span data-contract-field="fj211_02">項次</span>,
      dataIndex: 'fj211_02',
      key: 'fj211_02',
      width: 36,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: <span data-contract-field="fj211_11">產品代號</span>,
      dataIndex: 'fj211_11',
      key: 'fj211_11',
      width: 167,
      render: (val: string, _: any, index: number) =>
        isEditing ? (
          <Input
            value={val}
            onChange={e => handleCellChange(index, 'fj211_11', e.target.value)}
            maxLength={6}
            size="small" data-contract-field="fj211_11"
          />
        ) : (
          val
        ),
    },
    {
      title: <span data-contract-field="fj211_14">費用率%</span>,
      dataIndex: 'fj211_14',
      key: 'fj211_14',
      width: 71,
      render: (val: number, _: any, index: number) =>
        isEditing ? (
          <InputNumber
            value={val}
            onChange={v => handleCellChange(index, 'fj211_14', v ?? 0)}
            precision={1}
            size="small"
            style={{ width: '100%' }} data-contract-field="fj211_14"
          />
        ) : (
          val
        ),
    },
    {
      title: <span data-contract-field="fj211_15">毛利率%</span>,
      dataIndex: 'fj211_15',
      key: 'fj211_15',
      width: 76,
      render: (val: number, _: any, index: number) =>
        isEditing ? (
          <InputNumber
            value={val}
            onChange={v => handleCellChange(index, 'fj211_15', v ?? 0)}
            precision={2}
            size="small"
            style={{ width: '100%' }} data-contract-field="fj211_15"
          />
        ) : (
          val?.toFixed(2)
        ),
    },
    {
      title: <span data-contract-field="fj211_13">預估成本(NT$)</span>,
      dataIndex: 'fj211_13',
      key: 'fj211_13',
      width: 110,
      render: (val: number, _: any, index: number) =>
        isEditing ? (
          <InputNumber
            value={val}
            onChange={v => handleCellChange(index, 'fj211_13', v ?? 0)}
            precision={1}
            size="small"
            style={{ width: '100%' }} data-contract-field="fj211_13"
          />
        ) : (
          val?.toFixed(1)
        ),
    },
    {
      title: <span data-contract-field="fj211_16">建議售價(US$)</span>,
      dataIndex: 'fj211_16',
      key: 'fj211_16',
      width: 103,
      render: (val: number, _: any, index: number) =>
        isEditing ? (
          <InputNumber
            value={val}
            onChange={v => handleCellChange(index, 'fj211_16', v ?? 0)}
            precision={3}
            size="small"
            style={{ width: '100%' }} data-contract-field="fj211_16"
          />
        ) : (
          val?.toFixed(3)
        ),
    },
    {
      title: <span data-contract-field="fj211_17">單位</span>,
      dataIndex: 'fj211_17',
      key: 'fj211_17',
      width: 42,
      render: (val: string, _: any, index: number) =>
        isEditing ? (
          <Input
            value={val}
            onChange={e => handleCellChange(index, 'fj211_17', e.target.value)}
            maxLength={3}
            size="small" data-contract-field="fj211_17"
          />
        ) : (
          val
        ),
    },
    {
      title: <span data-contract-field="fj211_25">加工代號</span>,
      dataIndex: 'fj211_25',
      key: 'fj211_25',
      width: 235,
      render: (val: string, _: any, index: number) =>
        isEditing ? (
          <Input
            value={val}
            onChange={e => handleCellChange(index, 'fj211_25', e.target.value)}
            maxLength={30}
            size="small" data-contract-field="fj211_25"
          />
        ) : (
          val
        ),
    },
    ...(isEditing
      ? [
          {
            title: '操作',
            key: 'action',
            width: 70,
            render: (_: any, __: any, index: number) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => handleRemoveRow(index)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      {/* Header row: 總項數 + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <span data-contract-field="fj210_20"><strong>總項數：</strong>{formData.fj210_20 ?? details.length}</span>
        {isEditing && (
          <Button icon={<PlusOutlined />} size="small" onClick={handleAddRow}>
            新增明細
          </Button>
        )}
      </div>

      <Table
        dataSource={details.map((d, i) => ({ ...d, _key: i }))}
        columns={columns}
        rowKey="_key"
        size="small"
        pagination={false}
        locale={{ emptyText: '尚無明細資料' }}
        onRow={(_, index) => ({
          onClick: () => onSelectRow(index ?? 0),
          style: index === selectedRowIdx ? { background: '#e6f7ff' } : {},
        })}
      />

      {/* Below-grid detail fields — FoxPro two-column layout:
          Left: 3 textareas (產品名稱, 成品組織/規格, 加工別)
          Right: numerics, checkbox, lookups */}
      {sel && (
        <div style={{ marginTop: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 4 }}>
          <h4 style={{ marginBottom: 12 }}>明細欄位 (第 {selectedRowIdx + 1} 項)</h4>
          <Row gutter={24}>
            {/* Left column: textareas */}
            <Col span={12}>
              <Form layout="vertical">
                <Form.Item label="產品名稱">
                  {isEditing ? (
                    <Input.TextArea
                      data-contract-field="fj211_18"
                      value={sel.fj211_18}
                      onChange={e => handleDetailField('fj211_18', e.target.value)}
                      rows={2}
                      maxLength={60}
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{sel.fj211_18 || '—'}</span>
                  )}
                </Form.Item>

                <Form.Item label="成品組織/規格">
                  {isEditing ? (
                    <Input.TextArea
                      data-contract-field="fj211_19"
                      value={sel.fj211_19}
                      onChange={e => handleDetailField('fj211_19', e.target.value)}
                      rows={2}
                      maxLength={65}
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{sel.fj211_19 || '—'}</span>
                  )}
                </Form.Item>

                <Form.Item label="加工別">
                  {isEditing ? (
                    <Input.TextArea
                      data-contract-field="fj211_20"
                      value={sel.fj211_20}
                      onChange={e => handleDetailField('fj211_20', e.target.value)}
                      rows={2}
                      maxLength={60}
                    />
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{sel.fj211_20 || '—'}</span>
                  )}
                </Form.Item>
              </Form>
            </Col>

            {/* Right column: checkbox, lookups, numerics */}
            <Col span={12}>
              <Form layout="vertical">
                <Form.Item label="是否為暢銷品">
                  <Checkbox
                    checked={sel.fj211_27}
                    disabled={!isEditing}
                    onChange={e => handleDetailField('fj211_27', e.target.checked)} data-contract-field="fj211_27"
                  >
                    暢銷品
                  </Checkbox>
                </Form.Item>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="報告單號1">
                      {isEditing ? (
                        <LookupField
                          lookupType="sampleAnalysis"
                          value={(sel.fj211_261 as string) || ''}
                          onChange={(code) => handleDetailField('fj211_261', code)}
                          disabled={false}
                          title="搜尋報告單號" contractFieldId="fj211_261"
                        />
                      ) : (
                        <span>{sel.fj211_261 || '—'}</span>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="報告單號2">
                      {isEditing ? (
                        <LookupField
                          lookupType="sampleAnalysis"
                          value={(sel.fj211_262 as string) || ''}
                          onChange={(code) => handleDetailField('fj211_262', code)}
                          disabled={false}
                          title="搜尋報告單號" contractFieldId="fj211_262"
                        />
                      ) : (
                        <span>{sel.fj211_262 || '—'}</span>
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item label="報告單號3">
                  {isEditing ? (
                    <LookupField
                      lookupType="sampleAnalysis"
                      value={(sel.fj211_263 as string) || ''}
                      onChange={(code) => handleDetailField('fj211_263', code)}
                      disabled={false}
                      title="搜尋報告單號" contractFieldId="fj211_263"
                    />
                  ) : (
                    <span>{sel.fj211_263 || '—'}</span>
                  )}
                </Form.Item>

                {/* 針孔 內/外 — FoxPro label "針孔 內/外" */}
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="針孔內">
                      {isEditing ? (
                        <InputNumber
                          value={sel.fj211_21}
                          onChange={v => handleDetailField('fj211_21', v ?? 0)}
                          precision={1}
                          style={{ width: '100%' }} data-contract-field="fj211_21"
                        />
                      ) : (
                        <span>{sel.fj211_21}</span>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="針孔外">
                      {isEditing ? (
                        <InputNumber
                          value={sel.fj211_22}
                          onChange={v => handleDetailField('fj211_22', v ?? 0)}
                          precision={1}
                          style={{ width: '100%' }} data-contract-field="fj211_22"
                        />
                      ) : (
                        <span>{sel.fj211_22}</span>
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="碼重">
                      {isEditing ? (
                        <InputNumber
                          value={sel.fj211_23}
                          onChange={v => handleDetailField('fj211_23', v ?? 0)}
                          precision={1}
                          style={{ width: '100%' }} data-contract-field="fj211_23"
                        />
                      ) : (
                        <span>{sel.fj211_23}</span>
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="米平方重">
                      {isEditing ? (
                        <InputNumber
                          value={sel.fj211_24}
                          onChange={v => handleDetailField('fj211_24', v ?? 0)}
                          precision={1}
                          style={{ width: '100%' }} data-contract-field="fj211_24"
                        />
                      ) : (
                        <span>{sel.fj211_24}</span>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default Tab2Details;
